/**
 * Which of the people `/api/users/search` returned is the one the rep named.
 *
 * Pure functions, no I/O — the whole point is that the rule deciding whether to
 * auto-select is testable on its own, because being wrong here is expensive in
 * a way the rest of the sheet is not: a request raised against the wrong client
 * carries through to its quotes and its invoices.
 *
 * Two properties of the endpoint shape this:
 *
 *  1. It matches on email, phone, company and the client's STORE name as well
 *     as the person's name. So "returned by the search" is nowhere near "is the
 *     person who was named", and only a name-tier match may ever be auto-picked.
 *  2. `createBaseQuery()` uses `User::withTrashed()`, so soft-deleted people come
 *     back. A human skimming a dropdown can ignore one; auto-select cannot.
 */

/** Best tier first. WEAK is displayed but never auto-selected. */
const EXACT = 0;
const PARTIAL = 1;
const WEAK = 2;

/** Lowercase, unaccented, punctuation-free, single-spaced. */
const normalise = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

/**
 * The person's name alone. Deliberately NOT userOptionName: that appends
 * " | Store", and comparing against it would score everyone at a matching
 * store as a name match — which is one of the ways the endpoint matched them
 * in the first place.
 */
export const personName = (user) => {
  const built = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  if (built) return built;
  return String(user?.name ?? "").split("|")[0].trim();
};

const tierOf = (heardWords, user) => {
  const name = normalise(personName(user));
  if (!name) return WEAK;
  if (name === heardWords.join(" ")) return EXACT;

  const nameWords = name.split(" ");
  // Whole-word containment both ways round: "John" finds "John Doe", and
  // "John Doe" finds "John Doe Smith". A substring test would make "Jo" match
  // everyone, which is exactly the ambiguity this is meant to detect.
  return heardWords.every((word) => nameWords.includes(word)) ? PARTIAL : WEAK;
};

/**
 * @param {string} heard The client name the dictation reported.
 * @param {Array<object>} users Rows from /api/users/search.
 * @returns {{matches: Array<object>, auto: object|null}} `matches` are the
 *   people to offer, best first. `auto` is the one to select outright, or null.
 */
export const rankClientMatches = (heard, users, { limit = 5 } = {}) => {
  const heardWords = normalise(heard).split(" ").filter(Boolean);
  if (heardWords.length === 0) return { matches: [], auto: null };

  const scored = (Array.isArray(users) ? users : [])
    .filter((user) => user?.id != null && !user.deleted_at)
    .map((user, index) => ({ user, index, tier: tierOf(heardWords, user) }));

  // Stable within a tier: the endpoint's own order (most recent first) breaks
  // ties, rather than something arbitrary.
  scored.sort((a, b) => a.tier - b.tier || a.index - b.index);

  const best = scored.length ? scored[0].tier : null;
  const inBest = scored.filter((row) => row.tier === best);

  return {
    matches: scored.slice(0, limit).map((row) => row.user),
    // ONE person, matched on their NAME, and nobody else as good. Two Johns is
    // the normal case and must land the rep on a choice, not on a guess.
    auto: best !== null && best <= PARTIAL && inBest.length === 1 ? inBest[0].user : null,
  };
};
